import React, { useState, useRef, useEffect, createContext, useContext } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Skeleton, TreeItem, TreeView } from '@material-ui/lab';
import { setFilterMenuOpened } from 'actions/filtersMenu';
import {
  openObjectiveCreationModalAction,
  closeObjectiveCreationModalAction,
} from 'actions/objectiveCreationModalActions';
import { openRealignModalAction } from 'components/RealignModal/redux/actions';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { selectMSTeamsContext } from 'selectors/msTeamsSelector';
import styled from 'styled-components';
import { StyledButton, StyledTooltip, StyledTypography } from 'styles/common';
import { checkUserIsGoalAdmin } from 'utils/helpers/rolesAccess';

import { DeliverableIcon } from 'constants/icons';

import { getOrganizationTags } from 'services/admin';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';

const useStyles = makeStyles({
  root: {
    maxHeight: 350,
    flexGrow: 1,
    maxWidth: 450,
    minWidth: 200,
    padding: 8,
  },
  treeItem: {
    paddingTop: 4,
  },
});

export const ObjectiveDisplayContext = createContext({});

function ObjectiveDisplayName({ color, ...props }) {
  const { selectedObjective } = useContext(ObjectiveDisplayContext);
  return (
    <StyledTooltip title={selectedObjective.name}>
      <StyledTypography color={color} noWrap display="inline" variant="caption" {...props}>
        {selectedObjective.name}
      </StyledTypography>
    </StyledTooltip>
  );
}

function ObjectiveDisplayIcon({ color, size = 'tinier', rightGutter = 0.4, ...props }) {
  return <DeliverableIcon color={color} size={size} rightGutter={0.4} {...props} />;
}

export function ObjectiveDisplay({ children, className, ...props }) {
  const { id, buttonRef, onClick } = useContext(ObjectiveDisplayContext);
  return (
    <StyledButton
      aria-describedby={id}
      variant="oulined"
      color="primary"
      onClick={onClick}
      padding="0px"
      ref={buttonRef}
      className={className}
      {...props}
    >
      {children}
    </StyledButton>
  );
}
ObjectiveDisplay.Icon = ObjectiveDisplayIcon;
ObjectiveDisplay.Name = ObjectiveDisplayName;

const ObjectiveTreeViewContainer = ({
  className,
  children,
  tagName = 'Objective',
  selectedObjective = null,
  objectiveColor,
  objectiveIconColor,
  renderObjectiveDisplay,
  menuType,
  onClickAway,
  onObjectiveChange,
  enableCreation = false,
  allowTimePeriodChange = true,
  selectedTimeBoundPeriod = null,
  timeboundIsDisabled,
}) => {
  // when creating new goal/task selectedObjective will be null
  if (selectedObjective === null || selectedObjective === undefined) {
    selectedObjective = { name: 'Objective' };
  }

  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef();
  const dispatch = useDispatch();
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const id = isOpen ? 'simple-popover' : undefined;

  const closePopover = () => {
    setIsOpen(false);
    if (typeof onClickAway === 'function') {
      onClickAway();
    }
  };

  const openPopover = () => {
    setIsOpen(true);
  };

  const handleSelectTag = (tag) => {
    onObjectiveChange(tag);
    closePopover();
    dispatch(closeObjectiveCreationModalAction());
    dispatch(showSuccessSnackbar(`${objectivesName} selected successfully`));
  };

  useEffect(() => {
    if (menuType) {
      dispatch(setFilterMenuOpened(menuType, isOpen));
    }

    if (isOpen) {
      // open Popover is moved to here.
      dispatch(
        openRealignModalAction({
          selectedObjective,
          isSelectObjectiveModal: true,
          onSelect: handleSelectTag,
          selectedTimeBoundPeriod,
          onClose: closePopover,
          timeboundIsDisabled,
          allowTimePeriodChange,
          source: 'Tree view',
        })
      );
    }
  }, [isOpen]);

  const objectiveDisplayContext = {
    selectedObjective,
    id,
    buttonRef,
    onClick: openPopover,
    objectiveColor,
    objectiveIconColor,
  };

  return (
    <>
      <ObjectiveDisplayContext.Provider value={objectiveDisplayContext}>
        {renderObjectiveDisplay ? (
          renderObjectiveDisplay({ ref: buttonRef, isOpen })
        ) : (
          // For backward compatibility
          <ObjectiveDisplay className={className}>
            <ObjectiveDisplay.Icon color={objectiveIconColor} />
            <ObjectiveDisplay.Name color={objectiveColor} />
          </ObjectiveDisplay>
        )}
      </ObjectiveDisplayContext.Provider>
      {/* <Popover
  id={id}
  open={isOpen}
  anchorEl={buttonRef.current}
  onClose={closePopover}
  anchorOrigin={{
    vertical: 'bottom',
    horizontal: 'left'
  }}
  transformOrigin={{
    vertical: 'top',
    horizontal: 'left'
  }}
>
  {children({ closePopover })}
</Popover> */}
    </>
  );
};

const CREATE_NEW = -2;
const LOADING = -1;

const loadingTag = {
  id: LOADING,
  name: 'Loading...',
  subTags: [],
};

const CreateNewTag = {
  id: CREATE_NEW,
  name: '+ Create New',
  subTags: [],
};

const useObjectiveTreeState = (enableObjectiveCreation) => {
  const [tags, setTags] = useState(enableObjectiveCreation ? [CreateNewTag, loadingTag] : [loadingTag]);

  function updateTags(parentTag, subTags) {
    if (parentTag) {
      setTags((prevTags) => {
        parentTag.subTags = subTags;
        return [...prevTags];
      });
      return;
    }

    setTags(enableObjectiveCreation ? [CreateNewTag, ...subTags] : [...subTags]);
  }

  const getData = (parentTag) => {
    const parentTagId = parentTag ? parentTag.id : null;

    getOrganizationTags({
      teamId: -1,
      tagType: parentTagId ? 'SUB_OBJECTIVE_TAG' : 'GOAL', // need to move this outside somewhere
      orgTagId: parentTagId,
    }).then((allTags) => {
      const activeTags = allTags.filter((tag) => tag.active);
      const mappedTags = activeTags.map((tag) => ({
        ...tag,
        subTags: [
          {
            id: -1,
            name: '',
            subTags: [],
          },
        ],
      }));

      updateTags(parentTag, mappedTags);
    });
  };

  const reloadTags = () => {
    setTags(enableObjectiveCreation ? [CreateNewTag, loadingTag] : [loadingTag]);
    getData();
  };

  useEffect(() => {
    getData();
  }, []);

  return {
    tags,
    getData,
    reloadTags,
  };
};

const ObjectiveTreeView = ({
  className,
  selectedObjective = null,
  objectiveColor,
  objectiveIconColor,
  onObjectiveChange,
  enableObjectiveCreation = true,
  renderObjectiveDisplay,
  msTeamsContext,
  menuType,
  onClickAway,
  allowTimePeriodChange = true,
  selectedTimeBoundPeriod = null,
  timeboundIsDisabled,
}) => {
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const dispatch = useDispatch();
  const history = useHistory();

  const classes = useStyles();
  const [expanded, setExpanded] = useState([]);
  const [selected, setSelected] = useState([]);

  const enableCreation =
    enableObjectiveCreation === false || (msTeamsContext && Object.keys(msTeamsContext).length > 0)
      ? false
      : checkUserIsGoalAdmin() || loggedInEmployee.isHRISManager;
  const { tags, getData, reloadTags } = useObjectiveTreeState(
    msTeamsContext && Object.keys(msTeamsContext).length > 0 ? false : enableObjectiveCreation
  );

  const findTag = (id) => {
    const tagsChain = id.split('+++').reverse();

    let currentTagId = tagsChain[0];
    let subTags = tags;
    let returnTag;

    while (tagsChain.length > 0) {
      currentTagId = tagsChain.pop();
      returnTag = subTags.filter((tag) => tag.id === currentTagId)[0];
      subTags = returnTag.subTags;
    }

    return returnTag;
  };

  // only expand if icon was clicked
  const handleToggle = (event, nodeIds) => {
    const iconClicked = event.target.closest('.MuiTreeItem-iconContainer');
    if (iconClicked) {
      setExpanded(nodeIds);

      if (nodeIds.length > 0) {
        // check if need to make a call for data.
        const clickedTag = findTag(nodeIds[0]);
        if (clickedTag.subTags.length === 1 && clickedTag.subTags[0].id === LOADING) getData(clickedTag);
      }
    }
  };

  // only select if icon wasn't clicked
  const handleSelect = (event, nodeId, closePopover) => {
    const iconClicked = event.target.closest('.MuiTreeItem-iconContainer');
    if (!iconClicked) {
      if (nodeId === LOADING) return;

      if (nodeId === CREATE_NEW) handleCreateNewTag();
      else {
        const tag = findTag(nodeId);

        setExpanded([]);
        onObjectiveChange(tag);
      }
      closePopover();
    }
  };

  const handleCreateNewTag = () => {
    if (!checkUserIsGoalAdmin() && !loggedInEmployee.isHRISManager) {
      dispatch(showErrorSnackbar("You don't have required permission. Please contact admin."));
    } else {
      // close all expanded tags.
      setExpanded([]);
      dispatch(
        openObjectiveCreationModalAction({
          onClose: reloadTags,
        })
      );
    }
  };

  return (
    <ObjectiveTreeViewContainer
      // tagName={selectedTag.name}
      selectedObjective={selectedObjective}
      objectiveColor={objectiveColor}
      objectiveIconColor={objectiveIconColor}
      className={className}
      renderObjectiveDisplay={renderObjectiveDisplay}
      menuType={menuType}
      onClickAway={onClickAway}
      onObjectiveChange={onObjectiveChange}
      enableCreation={enableCreation}
      allowTimePeriodChange={allowTimePeriodChange}
      selectedTimeBoundPeriod={selectedTimeBoundPeriod}
      timeboundIsDisabled={timeboundIsDisabled}
    >
      {({ closePopover }) => (
        <TreeView
          className={classes.root}
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
          expanded={expanded}
          selected={selected}
          onNodeToggle={handleToggle}
          onNodeSelect={(event, nodeId) => handleSelect(event, nodeId, closePopover)}
        >
          {tags.map((tag) => (
            <ObjectiveItem key={tag.id} tag={tag} parentId={null} />
          ))}
        </TreeView>
      )}
    </ObjectiveTreeViewContainer>
  );
};

const ObjectiveItem = ({ tag: { id, subTags, name: label }, parentId }) => {
  const classes = useStyles();
  return (
    <>
      {id === LOADING ? (
        <TreeItem
          className={classes.treeItem}
          nodeId={id}
          label={
            <SkeletonContainer>
              <Skeleton height="0.5rem" width="90%" />
              <Skeleton height="0.5rem" width="90%" />
            </SkeletonContainer>
          }
        />
      ) : (
        <TreeItem
          className={classes.treeItem}
          nodeId={parentId ? `${parentId}+++${id}` : id}
          label={
            <LeftAlignedButton variant="text">
              <StyledTypography variant="body1">{label}</StyledTypography>
            </LeftAlignedButton>
          }
        >
          {subTags.map((tag) => (
            <ObjectiveItem key={tag.id} tag={tag} parentId={parentId ? `${parentId}+++${id}` : id} />
          ))}
        </TreeItem>
      )}
    </>
  );
};

const LeftAlignedButton = styled(StyledButton)`
  width: 100%;
  justify-content: flex-start;
  cursor: pointer;

  > span {
    * {
      cursor: pointer;
    }
  }
`;

const SkeletonContainer = styled.div`
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  cursor: default;
`;

const mapStateToProps = (state) => ({
  msTeamsContext: selectMSTeamsContext(state),
});

export default connect(mapStateToProps, {})(ObjectiveTreeView);
